import type { ReactNode } from 'react';
import { MetaList, type MetaItem } from './MetaList';
import { KpiValue } from './primitives';
import { cx } from './primitives/cx';
import { useShellTier } from '../../layouts/shellTier';

/**
 * P-076 (P3) · El resumen de 2–4 cifras prioritarias de un detalle o de un informe (Diferencia · Esperado ·
 * Contado; Venta bruta · Esperado · Contado · Diferencia). Dos cifras por renglón en el teléfono y hasta
 * cuatro en escritorio; el resto, plegado en «Ver más cifras» como lista etiqueta/valor.
 *
 * Compone `KpiValue` (no inventa otra cifra). Un estado es una insignia, no una cifra: va aparte. Las
 * advertencias de fiabilidad (cobertura, «aproximado», costo no disponible) no se pliegan: van junto a la
 * cifra que califican, con `hint`.
 */
export interface Stat {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'default' | 'muted' | 'danger' | 'success' | 'warning';
  key?: string;
}

export function StatSummary({ label, stats, more, moreLabel = 'Ver más cifras', plain = false, className }: {
  /** Nombre accesible del resumen («Resultado del corte»). */
  label: string;
  stats: Stat[];
  /** Cifras secundarias, plegadas. */
  more?: MetaItem[];
  moreLabel?: string;
  /** Dentro de una tarjeta o de una hoja: sin fondo propio. */
  plain?: boolean;
  className?: string;
}) {
  // Codex R2 · H3: en tableta y escritorio las cifras secundarias se ven desde el principio (plegadas solo en el teléfono).
  const tier = useShellTier();
  return (
    <section aria-label={label} className={cx('n3-stats', plain && 'n3-stats--plain', className)}>
      <div className="n3-stats__grid">
        {stats.map((stat, index) => (
          <KpiValue
            hint={stat.hint}
            key={stat.key ?? index}
            label={stat.label}
            value={stat.tone && stat.tone !== 'default' ? <span className={`n3-stats__value n3-stats__value--${stat.tone}`}>{stat.value}</span> : stat.value}
          />
        ))}
      </div>
      {more && more.length > 0 && (
        <details className="n3-stats__more" open={tier !== 'compact' || undefined}>
          <summary>{moreLabel}</summary>
          <MetaList columns items={more} />
        </details>
      )}
    </section>
  );
}
